import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, debounceTime, filter, map } from 'rxjs';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { AppState } from 'src/app/store/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PERMISSIONS_DEFAULT_SORT_COLUMN, PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION, PERMISSIONS_PAGE_DEFAULT_SIZE, PERMISSIONS_PAGE_SIZE_OPTIONS } from 'src/app/conf/permissions';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Permission, EPermissionStatus, PermittedCommand, PermittedThs } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectLoading } from 'src/app/store/user-file/user-file.selector';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectPermissions, selectPermissionsPaging, selectPermissionsAdded, selectPermissionsEdited, selectPermissionsDeleted, selectPermissionsPinResetted, selectPermittedCommands, selectPermittedThs } from 'src/app/store/permissions/permissions.selector';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { TranslateService } from '@ngx-translate/core';
import { PermissionInfoDialogComponent } from '../permission-info-dialog/permission-info.dialog.component';
import { PermissionDialogComponent } from '../permission-dialog/permission.dialog.component';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-permissions-list',
	templateUrl: './permissions-list.component.html',
	changeDetection: ChangeDetectionStrategy.Default,
	providers: [ConfirmationService, DialogService]
})

export class PermissionsListComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	permissionsLoading$: Observable<boolean | undefined>;
	permissionsLoadingSub: Subscription | undefined;

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	permissions$: Observable<Permission[] | undefined>;
	permissionsSub: Subscription | undefined;

	paging$: Observable<ItemsPagination | undefined>;
	pagingSub: Subscription | undefined;

	added$: Observable<boolean | undefined>;
	addedSub: Subscription | undefined;

	edited$: Observable<boolean | undefined>;
	editedSub: Subscription | undefined;

	deleted$: Observable<boolean | undefined>;
	deletedSub: Subscription | undefined;

	pinResetted$: Observable<boolean | undefined>;
	pinResettedSub: Subscription | undefined;

	permissionsLoading: boolean;
	permissions: Permission[];
	site: Site;

	filterForm: FormGroup;
	filter: '';
	activeFilter: true;
	readyFilter: true;
	expiredFilter: true;
	createdFilter: true;

	filterSub: Subscription | undefined;
	activeFilterSub: Subscription | undefined;
	readyFilterSub: Subscription | undefined;
	expiredFilterSub: Subscription | undefined;
	createdFilterSub: Subscription | undefined;

	paging: PaginationParams = {
		size: PERMISSIONS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: PERMISSIONS_DEFAULT_SORT_COLUMN,
		order: PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = PERMISSIONS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = PERMISSIONS_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	ref: DynamicDialogRef | undefined;

	permittedCommands: PermittedCommand[] = [];
	permittedCommands$: Observable<PermittedCommand[] | undefined>;
	permittedCommandsSub: Subscription | undefined;

	permittedThs: PermittedThs[] = [];
	permittedThs$: Observable<PermittedThs[] | undefined>;
	permittedThsSub: Subscription | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	addPermissionConfDialog = false;

	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	// Design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.permissionsLoading$ = this.store.select(selectLoading);
		this.site$ = this.store.select(selectCurrentSite);
		this.permissions$ = this.store.select(selectPermissions);
		this.paging$ = this.store.select(selectPermissionsPaging);
		this.added$ = this.store.select(selectPermissionsAdded);
		this.edited$ = this.store.select(selectPermissionsEdited);
		this.deleted$ = this.store.select(selectPermissionsDeleted);
		this.pinResetted$ = this.store.select(selectPermissionsPinResetted);
		this.permittedCommands$ = this.store.select(selectPermittedCommands);
		this.permittedThs$ = this.store.select(selectPermittedThs);
	}

	ngOnInit(): void {
		this.filterForm = this.formBuilder.group({
			searchFilter: [''], activeFilter: true,
			readyFilter: true, expiredFilter: true, createdFilter: true
		});

		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.permissionsLoadingSub?.unsubscribe();
		this.permissionsSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.pagingSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.deletedSub?.unsubscribe();
		this.pinResettedSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.activeFilterSub?.unsubscribe();
		this.readyFilterSub?.unsubscribe();
		this.expiredFilterSub?.unsubscribe();
		this.createdFilterSub?.unsubscribe();
		this.permittedCommandsSub?.unsubscribe();
		this.permittedThsSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.permissionsLoadingSub = this.permissionsLoading$?.subscribe(
			(permissionsLoading) => {
				this.permissionsLoading = permissionsLoading;
			});

		this.permittedCommandsSub = this.permittedCommands$.subscribe(permittedCommands => {
			this.permittedCommands = permittedCommands;
		});
		this.permissionsSub = this.permissions$?.subscribe(
			(permissions) => {
				this.permissions = permissions;
			});

		this.siteSub = this.site$.pipe(filter(s => !!s)).subscribe((site) => {
			this.site = site;
			this.getPermissions();
			this.store.dispatch(PermissionActions.getPermittedCommands());
			this.store.dispatch(PermissionActions.getPermittedThs());
		});
		this.permittedThsSub = this.permittedThs$.subscribe(permittedThs => {
			this.permittedThs = permittedThs;
		});

		this.pagingSub = this.paging$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.addedSub = this.added$.pipe(filter(added => !!added))
			.subscribe(() => {
				this.getPermissions();
			});

		this.editedSub = this.edited$.pipe(filter(edited => !!edited))
			.subscribe(() => {
				this.getPermissions();
			});

		this.deletedSub = this.deleted$.pipe(filter(deleted => !!deleted))
			.subscribe(() => {
				this.getPermissions();
			});

		this.pinResettedSub = this.pinResetted$.pipe(filter(reset => !!reset))
			.subscribe(() => {
				this.getPermissions();
			});

		this.filterSub = this.filterForm.controls['searchFilter'].valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.getPermissions();
			// this.paginator.firstPage();
		});

		this.activeFilterSub = this.filterForm.controls['activeFilter'].valueChanges.subscribe(value => {
			this.activeFilter = value;
			this.getPermissions();
			// this.paginator.firstPage();
		});

		this.readyFilterSub = this.filterForm.controls['readyFilter'].valueChanges.subscribe(value => {
			this.readyFilter = value;
			this.getPermissions();
			// this.paginator.firstPage();
		});

		this.expiredFilterSub = this.filterForm.controls['expiredFilter'].valueChanges.subscribe(value => {
			this.expiredFilter = value;
			this.getPermissions();
			// this.paginator.firstPage();
		});

		this.createdFilterSub = this.filterForm.controls['createdFilter'].valueChanges.subscribe(value => {
			this.createdFilter = value;
			this.getPermissions();
			// this.paginator.firstPage();
		});
	}


	expired(permission: Permission): boolean {
		return permission.Status === EPermissionStatus.Expired;
	}

	editPermission(permission: Permission): void {
		this.store.dispatch(PermissionActions.resetPermissionForm());
		this.ref = this.dialogService.open(PermissionDialogComponent, {
			header: this.translate.instant('EDIT_PERMISSION'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				permission: permission
			}
		});
		/* dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
			if (result === 'success') {
				this.store.dispatch(PermissionActions.getPermissions({ paging: this.paging, filter: this.filter,
					activeFilter: this.activeFilter, readyFilter: this.readyFilter,
					expiredFilter: this.expiredFilter, createdFilter: this.createdFilter}));
			}
		});*/
	}

	info(permission: Permission): void {
		this.store.dispatch(PermissionActions.resetPermissionForm());
		this.ref = this.dialogService.open(PermissionInfoDialogComponent, {
			header: this.translate.instant('PERMISSION') + ': ' + permission.Name,
			width: '50vw',
			breakpoints: { '950px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { permission: permission }
		});
	}

	getVisiblePin(permission: Permission): boolean {
		return permission.PinVisible;
	}

	setVisiblePin(permission: Permission) {
		if (permission.PinVisible === undefined) {
			permission.PinVisible = false;
		}
		permission.PinVisible = !permission.PinVisible;
	}

	resetPin(permissionId: number) {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('RESET_PERMISSION_PIN'),
			message: this.translate.instant('RESET_PERMISSION_PIN_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.resetPin({ permissionId }));
			},
			reject: () => {
			}
		});
		/*const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			minWidth: '20vw',
			maxWidth: '50vw',
			data: { title: 'RESET_PERMISSION_PIN', body: 'RESET_PERMISSION_PIN_WARNING' }
		});
		this.confirmResetSub = dialogRef.afterClosed().subscribe(result => {
			if (result === 'success') {
				this.store.dispatch(PermissionActions.resetPin({ permissionId }));
			}
		});*/
	}

	deletePer(permission: Permission): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_PERMISSION'),
			message: this.translate.instant('DELETE_PERMISSION_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.deletePermission({ permission }));
			},
			reject: () => {
			}
		});
		/* const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
				minWidth: '20vw',
				maxWidth: '50vw',
				data: { title: 'DELETE_PERMISSION', body: 'DELETE_PERMISSION_WARNING' }
			});
			this.confirmDeleteSub = dialogRef.afterClosed().subscribe(result => {
				if (result === 'success') {
					this.store.dispatch(PermissionActions.deletePermission({ permission }));
				}
			});*/
	}

	getColorStatus(status: string): string {
		switch (status) {
			case 'expired':
				return 'danger';
			case 'active':
				return 'success';
			case 'ready':
				return 'warning';
			default:
				return 'info';
		}
	}

	loadPermissions(event: TableLazyLoadEvent) {
		//this.customersLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.getPermissions();
	}

	getPermissions() {
		if (this.site !== undefined) {
			this.store.dispatch(PermissionActions.getPermissions({
				paging: this.paging, filter: this.filter,
				activeFilter: this.activeFilter, readyFilter: this.readyFilter,
				expiredFilter: this.expiredFilter, createdFilter: this.createdFilter
			}));
		}
	}

	isFinalUsers(): boolean {
		return this.site !== undefined && this.site !== null && !this.site.CurrentlyOwned;
	}

	getCurrentLanguageFlag(customerLanguage): string {
		if (customerLanguage === 'en') { // default if field is empty
			customerLanguage = 'en-US';
		}
		const currentLangFlagFile = this.flagImagePath + customerLanguage + this.flagImageFormat;
		return currentLangFlagFile;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}
}